import React, { FC, ReactNode, useContext, useMemo, useState } from 'react';

import IconEditorMenuButton from '../../../../components/buttons/iconEditorMenuButton';
import styled from 'styled-components';

//constants
import aampeTheme from '../../../../constants/aampeTheme';
import ICONS from '../../../../constants/icons';

//context
import { EditorContext } from '../../../../context/EditorContext';

//hooks
import useDatasetByIdQuery from '../../../../hooks/useDatasetByIdQuery';

import { DatasetFieldModel } from '../../../../models/dataset.model';
//models
import { DatasetElement } from '../../../../models/editor.model';

import DatasetConfigurationalModalV1 from '../../../../views/formulaCreate/formulaEditor/assistant/datasets/datasetConfigurationalModal/DatasetConfigurationalModalV1';

//components
import DatasetElementView from './DatasetElementView';

interface DatasetIconDropDownProps {
    element?: DatasetElement;
    handleDatasetFieldClick: (id: number) => void;
    showIcon?: boolean;
    isIconDisabled?: boolean;
}

const DatasetIconDropDown: FC<DatasetIconDropDownProps> = ({
    handleDatasetFieldClick,
    element,
    showIcon,
    isIconDisabled,
}) => {
    const {
        editorDetails: { formula },
    } = useContext(EditorContext);

    const [
        showDatasetConfigurationalModal,
        setShowDatasetConfigurationalModal,
    ] = useState<boolean>(false);

    const [selectedFieldId, setSelectedFieldId] = useState<
        string | undefined
    >();

    const { data } = useDatasetByIdQuery(formula?.dataset_id!);

    const datasetField = useMemo<DatasetFieldModel>(
        () =>
            data && data.fields
                ? data.fields.find(
                      (field: DatasetFieldModel) =>
                          field.id === element?.datasetField,
                  )
                : null,
        [data, element],
    );

    const handleClick = () => {
        setSelectedFieldId(
            datasetField?.id !== undefined
                ? String(datasetField.id)
                : undefined,
        );

        setShowDatasetConfigurationalModal(true);
    };

    const datasetValue = useMemo<string | string[] | null>(
        () => (datasetField ? datasetField.sample : null),
        [datasetField],
    );
    const isMultiValue = useMemo<boolean>(
        () => (datasetValue ? Array.isArray(datasetValue) : false),
        [datasetValue],
    );

    const fieldString = useMemo<string>(() => {
        if (isMultiValue) {
            return datasetValue?.[0] as string;
        } else {
            return datasetValue as string;
        }
    }, [datasetValue, isMultiValue]);

    const AampeToolTipTitle = useMemo<ReactNode | undefined>(() => {
        if (data?.name && datasetField) {
            return (
                <ToolTipText>
                    <Text>Dataset name: {data?.name}</Text>
                    <Text>Field name: {datasetField?.name}</Text>
                    <Text>Sample: {fieldString}</Text>
                </ToolTipText>
            );
        }
        return undefined;
    }, [data, datasetField, fieldString]);

    return (
        <>
            {showIcon ? (
                <IconEditorMenuButton
                    Icon={ICONS.datasets}
                    isDisabled={isIconDisabled}
                    action={handleClick}
                    tooltip="Insert Dataset"
                />
            ) : (
                <StyledDatasetElementView
                    datasetFieldSample={fieldString as string}
                    aampeToolTipTitle={AampeToolTipTitle}
                    handleClick={handleClick}
                />
            )}

            {showDatasetConfigurationalModal && (
                <DatasetConfigurationalModalV1
                    handleClose={() => {
                        setShowDatasetConfigurationalModal(false);
                        setSelectedFieldId(undefined);
                    }}
                    showColumnSelect
                    handleColumnSelect={(id) => {
                        handleDatasetFieldClick(Number(id));
                    }}
                    selectedFieldId={selectedFieldId}
                    setSelectedFieldId={setSelectedFieldId}
                />
            )}
        </>
    );
};

const StyledDatasetElementView = styled(DatasetElementView)({
    userSelect: 'none',
    outline: 'none',
    display: 'inline-block',
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.interactiveWG01,
    padding: aampeTheme.spacing4(0, 1),
    color: aampeTheme.colors.neutral09,
    lineHeight: '24px',
    fontSize: aampeTheme.size.font.default,
    fontWeight: 400,
    minWidth: '30px',
    border: `1px solid ${aampeTheme.colors.solidA02}`,
});

const Text = styled.div({
    fontSize: aampeTheme.size.font.default,
    lineHeight: '24px',
    fontWeight: 400,
});

const ToolTipText = styled.div({
    display: 'flex',
    flexDirection: 'column',
    padding: aampeTheme.spacing4(2),
});
export default DatasetIconDropDown;
